import {FC, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
//formik
import {Formik} from 'formik';
import {AddTableTicketFormValues, initialValues, validationSchema} from './formConfiguration';
//mui
import {Box, useMediaQuery} from '@mui/material';
//store
import {useAppSelector} from '@/store/hooks';
//context
import {AddTableTicketContext, AddTableTicketContextType} from './OrganizationSignUpContext';
//service
import {OrganizationTableAndTicketsService} from '@/utils/services';
//helpers
import {ADD_TABLE_TICKET_STEP, addTableTicketSteps, getNewTableTicketValues} from './helpers';
import {getErrorMessage} from '@/utils/helpers';
//types
import {IOrganizationTablesAndTicketsRequest} from '@/utils/types';
//components
import Stepper from '@/shared/components/Stepper';
import TicketsInfo from './TicketsInfo';
import PaymentTicketsInfo from './PaymentTicketsInfo';
import AttendeesInfo from './AttendeesInfo';
//styles
import {addTableTicketStyle} from './AddTableTicket.styles';
import {useNavigate} from 'react-router-dom';
import {getUserSelector} from '@/store/user';
import {getCurrentEventSelector} from '@/store/organization-event';
import {PAYMENT_METHOD, USER_TYPE} from '@/utils/enums';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {useGetOrganizationId} from '@/utils/hooks';
import {orgAPI, userAPI} from '@/core/api';

interface AddTableTicketProps {
	onCloseModal: () => void;
	onSuccess?: () => void;
}

const AddTableTicket: FC<AddTableTicketProps> = ({onCloseModal, onSuccess}) => {
	const navigate = useNavigate();
	const [activeStep, setActiveStep] = useState(0);

	const user = useAppSelector(getUserSelector);
	const event = useAppSelector(getCurrentEventSelector);

	const {apiInstance, role} = useApiInstance();

	const organizationId = useGetOrganizationId();

	const isLaptop = useMediaQuery('(min-width:1200px)');

	const handleNextStep = () => setActiveStep(activeStep + 1);
	const handleBackStep = () => setActiveStep(activeStep - 1);

	const formData = useMemo(() => {
		switch (activeStep) {
			case ADD_TABLE_TICKET_STEP.TICKET_INFO:
				return <TicketsInfo />;

			case ADD_TABLE_TICKET_STEP.PAYMENT:
				return <PaymentTicketsInfo />;

			case ADD_TABLE_TICKET_STEP.ATTENDEES_INFO:
				return <AttendeesInfo />;

			default:
				break;
		}
	}, [activeStep]);

	const submitClickHandler = async (values: AddTableTicketFormValues) => {
		if (!organizationId) return;

		const newTicketValues: IOrganizationTablesAndTicketsRequest = getNewTableTicketValues(values);
		if (activeStep === ADD_TABLE_TICKET_STEP.ATTENDEES_INFO) {
			if (values.paymentOption === PAYMENT_METHOD.CREDIT_CARD) {
				const data = await startStripeSession(newTicketValues);

				if (data?.url) {
					window.location.href = data.url;
				}
			} else {
				try {
					await OrganizationTableAndTicketsService.createTableAndTickets(newTicketValues, orgAPI);
					onCloseModal();
					navigate('/organization/tables');
				} catch (error) {
					toast.error(getErrorMessage(error));
				}
			}
		} else {
			handleNextStep();
		}
	};

	const startStripeSession = async (dataReq: IOrganizationTablesAndTicketsRequest) => {
		try {
			const {data} = await OrganizationTableAndTicketsService.prepareStripeSessionForTickets(dataReq, apiInstance);

			return data;
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const submitAttendeeClickHandler = async (values: AddTableTicketFormValues) => {
		if (!user || !event) return;

		const newTicketValues: IOrganizationTablesAndTicketsRequest = getNewTableTicketValues(values);

		if (activeStep === ADD_TABLE_TICKET_STEP.ATTENDEES_INFO) {
			if (values.paymentOption === PAYMENT_METHOD.CREDIT_CARD) {
				const data = await startStripeSession(newTicketValues);

				if (data?.url) {
					window.location.href = data.url;
				}
			} else {
				try {
					await OrganizationTableAndTicketsService.createTableAndTickets(newTicketValues, userAPI);
					onCloseModal();
					onSuccess?.();
				} catch (error) {
					toast.error(getErrorMessage(error));
				}
			}
		} else {
			handleNextStep();
		}
	};

	const AddTableTicketContextValue: AddTableTicketContextType = useMemo(() => {
		return {
			activeStep,
			onCloseModal,
			handleBackStep,
		};
	}, [activeStep, onCloseModal, handleBackStep]);

	useEffect(() => {
		if (!isLaptop) {
			onCloseModal();
		}
	}, [isLaptop]);

	return (
		<AddTableTicketContext.Provider value={AddTableTicketContextValue}>
			<Box sx={addTableTicketStyle.wrapper}>
				<Stepper steps={addTableTicketSteps} activeStep={activeStep} sx={addTableTicketStyle.stepper} />

				<Formik
					initialValues={initialValues}
					onSubmit={role === USER_TYPE.ORGANIZATION ? submitClickHandler : submitAttendeeClickHandler}
					validationSchema={validationSchema(activeStep)}
					validateOnChange={false}
				>
					{formData}
				</Formik>
			</Box>
		</AddTableTicketContext.Provider>
	);
};

export default AddTableTicket;
